// ════════════════════════════════════════════════════════════════════
// W'ILY'S PIER — ieSkies.jsx   ·   ONE SKY, WORN EVERYWHERE
// ────────────────────────────────────────────────────────────────────
// ⭐ caP'n, 2026-09-25: "when you use one sky, it should also appear in the
// unstable pier (and eventually any other game where you can customize the
// sky like we can here)."
//
// So a sky is worn ONCE — `homes/{uid}/minerium/sky` — and every screen that
// has a horizon reads it: the Minerium yard, the boardwalk, and any future
// title. This file is the recipe book both of them read (lifted out of
// Minerium3D.jsx, where it was the yard's private table). A new game gets
// the whole wardrobe by importing `skyWorldProps` and `skyDomeTex`.
//
// ⚑ A "standing" sky (Open Unstable Sky and its Remaster) is the pier's own
// horizon: on the boardwalk it draws exactly what the pier always drew
// (the remaster swapping in its Blender maps). Every other sky brings its
// own dome painting and its own reading of the Unstable World.
// ════════════════════════════════════════════════════════════════════
import { mulberry32, canvasTex } from "./ieBeingModels";

// THE CANON COLOURS, the same eight names the pier uses.
const C = {
  nullus: "#B388FF", omnis: "#F2C14E", anima: "#FF4D8D", chao: "#7CE7FF",
  lyt: "#FFE9A0", lyf: "#7FE8D8", ruin: "#E8453C", shade: "#2A1540",
};

export const DEFAULT_SKY = "sky_default";

// ════════════════════════════════════════════════════════════════════
// ⭐ THE SKY  —  one purchase, a completely different place to stand
// ────────────────────────────────────────────────────────────────────
// Each recipe is a painted dome plus a handful of measured elements,
// every one of them BasicMaterial with `fog={false}` and no light. The
// whole horizon is therefore free: it costs one draw call per element
// and nothing at all per frame except the two that turn.
// ════════════════════════════════════════════════════════════════════
export const SKY_RECIPES = {
  sky_default: {
    // Open Unstable Sky — the fallback horizon. What is out there when
    // nobody has decided otherwise.
    stops: [[0, "#05030f"], [0.44, "#0b0620"], [0.74, "#150a2c"], [1, "#04030a"]],
    bands: [C.nullus, C.omnis], stars: 220,
    eclipse: { r: 0.16, tint: C.omnis, crack: C.nullus, y: 0.62, dist: 0.78 },
    rise: false, sea: null, holes: 2,
    // the standing horizon, exactly as the pier has it
    reading: null, fractures: 14, glow: null, standing: true,
  },
  // ⭐⭐ THE REMASTER (caP'n, 2026-09-25): Open Unstable Sky, rebuilt in
  // Blender — "a ps1 game getting a ps3 remaster". The same horizon as
  // sky_default; `hd` swaps in the rendered dome, eclipse and Ruin Hole.
  pier_sky_remaster: {
    stops: [[0, "#05030f"], [0.44, "#0b0620"], [0.74, "#150a2c"], [1, "#04030a"]],
    bands: [C.nullus, C.omnis], stars: 220,
    eclipse: { r: 0.16, tint: C.omnis, crack: C.nullus, y: 0.62, dist: 0.78 },
    rise: false, sea: null, holes: 2,
    reading: null, fractures: 14, glow: null,
    hd: { dome: "skies/pier_remaster_dome.webp", eclipse: "skies/pier_remaster_eclipse.webp", hole: "skies/pier_remaster_hole.webp" },
    standing: true,     // the pier's own horizon — see skyWorldProps
  },
  // ⭐⭐ THE EYE (caP'n, 2026-09-25): "a massive eyeball to the point where
  // there is almost no white of the eye, maybe some on the horizon, but the
  // entire sky is the galaxy looking iris … the pupil at the very top center
  // of the dome and it will look like a black hole, so you can't even really
  // tell its an eye." Blender: gamemaster/models/sculpt/builders/
  // pier_sky_blender/build_eye_sky.py. The pupil IS the black hole, so the
  // reading hangs no eclipse and no holes; the rest of the Unstable World
  // (shards, the Lyt/Lyf pair, the ocean, THE RISE) stays out there.
  sky_eye: {
    stops: [[0, "#000000"], [0.2, "#1a0610"], [0.5, "#0a0418"], [0.8, "#06101a"], [1, "#050306"]],
    bands: [C.nullus, C.chao], stars: 360,
    eclipse: { r: 0, tint: null, crack: null, y: 0.9, dist: 0.6 },
    rise: true, sea: C.chao, holes: 0,
    reading: {
      ocean: "chao", eclipse: { show: false }, holes: [],
      shards: 0.7, shardHot: [C.nullus, C.chao], columns: 0.5,
      motes: 0.8, moteCols: [C.omnis, C.nullus, C.chao],
    },
    fractures: 0, glow: null,
    hd: { dome: "skies/eye_dome.webp" },
  },
  sky_dusk: {
    // Long Dusk — the hour before the eclipse cracks, held forever.
    stops: [[0, "#1a0c14"], [0.3, "#4a1a3a"], [0.55, "#a8404a"], [0.72, "#f08a4a"], [1, "#2a0c14"]],
    bands: [C.omnis, "#ff7a8a"], stars: 40,
    eclipse: { r: 0.2, tint: "#ffb04a", crack: null, y: 0.34, dist: 0.82 },
    rise: false, sea: null, holes: 0,
    // ⭐ A DIFFERENT PLACE (caP'n, 2026-09-16): no black hole, no cracked
    // eclipse yet — a WHOLE sun sinking into an ocean of Anima with the
    // bruise only beginning to bite it, rose and amber cloud streaks
    // drifting across, the shards gone to dark silhouettes, no columns
    reading: {
      ocean: "dusk", eclipse: { show: false },
      holes: [], shards: 0.55, shardGlow: false, columns: 0,
      motes: 0.35, moteCols: ["#ffb04a", "#ff8a8a"],
      sun: { core: "#fff0b0", edge: "#ff6a3a", size: 2.6, y: 0.62, x: 0.1 },   // on the horizon line, above the fence (visual check)
      clouds: { colors: ["#ff8a5a", "#d4507a", "#8a3a8a", "#ffb07a"], count: 16 },
    },
    fractures: 3, glow: "#ff8a4a",
  },
  sky_eclipse: {
    // Cracked Eclipse — rimmed in O'mnis, cracked by N'ullus, directly
    // overhead. The canonical one.
    stops: [[0, "#02010a"], [0.38, "#0a0418"], [0.7, "#1c0a36"], [1, "#05020c"]],
    bands: [C.nullus, C.chao], stars: 320,
    eclipse: { r: 0.3, tint: C.omnis, crack: C.nullus, y: 0.86, dist: 0.62 },
    rise: false, sea: C.chao, holes: 3,
    // ⭐ the wound itself: the eclipse HUGE and high, twin VIOLET holes
    // either side of it, the shards crowding in, THE RISE thin and cold
    reading: {
      ocean: "chao",
      eclipse: { x: 0, y: 1.05, z: -0.95, size: 2.4, rim: C.omnis, crack: C.nullus },   // ≤ 2.4R: measured inside the dome
      holes: [
        { x: -1.0, y: 0.55, z: -0.55, size: 0.75, hot: C.nullus, warm: "#d9b8ff" },
        { x: 1.0, y: 0.7, z: -0.6, size: 1.05, hot: C.nullus, warm: "#8a5aff" },
      ],
      shards: 1.7, shardHot: [C.nullus, "#e0d0ff"], columns: 0.55,
      motes: 0.6, moteCols: [C.chao, "#e0d0ff"],
    },
    fractures: 34, glow: C.nullus,
  },
  sky_the_rise: {
    // THE RISE, risen. The Mythical, so it is the Cracked Eclipse plus
    // motion and flare — the same shelf rule the furniture follows.
    stops: [[0, "#03010c"], [0.32, "#12052a"], [0.62, "#3a0e4a"], [1, "#0a0316"]],
    bands: [C.nullus, C.anima], stars: 420,
    eclipse: { r: 0.34, tint: C.lyt, crack: C.anima, y: 0.9, dist: 0.58 },
    rise: true, sea: C.anima, holes: 4,
    // ⭐ the Cracked Eclipse, RISEN: its wound pulses, a colossal pillar of
    // Lyt climbs behind it out of an ocean of Anima, red and violet holes
    // turn against each other, and THE RISE is a storm of gold and rose
    reading: {
      ocean: "anima",
      eclipse: { x: 0, y: 1.05, z: -0.95, size: 2.4, rim: C.lyt, crack: C.anima, pulse: true },
      holes: [
        { x: -1.05, y: 0.5, z: -0.5, size: 0.8, hot: C.ruin, warm: "#ff9d6b" },
        { x: 1.05, y: 0.75, z: -0.55, size: 1.0, hot: C.nullus, warm: "#d9b8ff" },
        { x: -0.6, y: 1.3, z: -0.9, size: 0.5, hot: C.anima, warm: "#ffb0d0" },
      ],
      shards: 1.3, shardHot: [C.anima, C.lyt], columns: 2,
      motes: 3, moteCols: [C.lyt, C.anima, C.omnis], moteSize: 1,
      pillar: { color: C.lyt, core: "#fffbe8" },
    },
    fractures: 20, glow: C.anima,
  },
};

export function skyDomeTex(skyId, spec) {
  return canvasTex(`min_sky_v2_${skyId}`, 1024, 1024, (ctx, S) => {
    const g = ctx.createLinearGradient(0, 0, 0, S);
    for (const [at, col] of spec.stops) g.addColorStop(at, col);
    ctx.fillStyle = g; ctx.fillRect(0, 0, S, S);

    const R = mulberry32(0xA11E ^ skyId.length * 7919);
    // nebula bands, 5–14% alpha, exactly the pier's recipe
    for (let i = 0; i < 7; i++) {
      const y = R() * S, h = S * (0.05 + R() * 0.16);
      const gg = ctx.createLinearGradient(0, y - h, 0, y + h);
      const col = spec.bands[i % spec.bands.length];
      gg.addColorStop(0, col + "00");
      gg.addColorStop(0.5, col + Math.floor(13 + R() * 22).toString(16).padStart(2, "0"));
      gg.addColorStop(1, col + "00");
      ctx.fillStyle = gg; ctx.fillRect(0, y - h, S, h * 2);
    }
    // a horizon glow, where a reading has one (the dusk's sun, the Rise's light)
    if (spec.glow) {
      const hg = ctx.createLinearGradient(0, S * 0.5, 0, S * 0.8);
      hg.addColorStop(0, spec.glow + "00"); hg.addColorStop(0.7, spec.glow + "55"); hg.addColorStop(1, spec.glow + "00");
      ctx.fillStyle = hg; ctx.fillRect(0, S * 0.5, S, S * 0.3);
    }
    // hairline fractures — the same wound as the eclipse, running out
    ctx.lineWidth = 1;
    for (let i = 0; i < (spec.fractures ?? 14); i++) {
      ctx.strokeStyle = (i % 3 ? spec.bands[0] : C.chao) + "22";
      ctx.beginPath();
      let x = R() * S, y = R() * S;
      ctx.moveTo(x, y);
      for (let s = 0; s < 6; s++) { x += (R() - 0.5) * 220; y += (R() - 0.5) * 160; ctx.lineTo(x, y); }
      ctx.stroke();
    }
    // stars
    for (let i = 0; i < spec.stars; i++) {
      const a = 0.2 + R() * 0.7;
      ctx.fillStyle = `rgba(240,232,208,${a.toFixed(3)})`;
      const s = R() > 0.94 ? 2.4 : 1.2;
      ctx.fillRect(R() * S, R() * S * 0.82, s, s);
    }
  });
}


// ════════════════════════════════════════════════════════════════════
// WHAT A HORIZON NEEDS TO WEAR A SKY — props for `UnstableWorld3D`.
// `domeTex` is set only for a sky with its own painting; a standing sky
// leaves the screen's own dome alone (the pier's 620-star canvas), so the
// default looks exactly as it always did.
// ════════════════════════════════════════════════════════════════════
export function skyWorldProps(skyId) {
  const spec = SKY_RECIPES[skyId] || SKY_RECIPES[DEFAULT_SKY];
  return {
    reading: spec.reading || null,
    tint: spec.standing ? null : (spec.reading ? null : (spec.eclipse?.tint || null)),
    rise: spec.reading ? true : spec.rise !== false,
    hd: spec.hd || null,
    standing: !!spec.standing,
    domeTex: spec.standing ? null : skyDomeTex(SKY_RECIPES[skyId] ? skyId : DEFAULT_SKY, spec),
  };
}
